import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Search, User, Mail, Plus, Check, AlertCircle, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin component for booking appointments on behalf of users
 */
const AdminAppointmentBooking = ({ onClose, onSuccess }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [step, setStep] = useState(1); // 1: Select User, 2: Select Time, 3: Confirm
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredUsers(users.filter(user => 
        user.email?.toLowerCase().includes(query) ||
        user.profile?.first_name?.toLowerCase().includes(query) ||
        user.profile?.last_name?.toLowerCase().includes(query)
      ));
    }
  }, [searchQuery, users]);

  // Fetch available slots when user is selected
  useEffect(() => {
    if (selectedUser) {
      fetchAvailableSlots();
    }
  }, [selectedUser, currentMonth]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/admin/override/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // API returns { users: [...] }
        const usersList = data.users || data;
        // Filter to only show students (not admins/mentors)
        const studentUsers = usersList.filter(u => u.role !== 'admin' && u.role !== 'mentor');
        setUsers(studentUsers);
        setFilteredUsers(studentUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const token = localStorage.getItem('access_token');
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      
      const response = await fetch(
        `${API_URL}/api/appointments/available-slots?month=${month}&year=${year}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const slots = await response.json();
        setAvailableSlots(slots.filter(slot => slot.available));
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Failed to load available slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleBookAppointment = async () => {
    if (!selectedUser || !selectedSlot) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/appointments/admin/book-for-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          date: selectedSlot.date,
          time: selectedSlot.time,
          duration: 30,
          type: 'mentorship',
          notes: notes || 'Scheduled by admin'
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Appointment booked for ${selectedUser.email}`);
        onSuccess?.();
        onClose?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to book appointment');
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error('Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const getUserDisplayName = (user) => {
    if (user.profile?.first_name || user.profile?.last_name) {
      return `${user.profile.first_name || ''} ${user.profile.last_name || ''}`.trim();
    }
    return user.email;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const groupSlotsByDate = () => {
    const grouped = {};
    availableSlots.forEach(slot => {
      if (!grouped[slot.date]) {
        grouped[slot.date] = [];
      }
      grouped[slot.date].push(slot);
    });
    return grouped;
  };

  const changeMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="admin-booking-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-purple-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Book Appointment for User</h2>
            <p className="text-sm text-gray-600">Step {step} of 3</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-purple-100 rounded-full transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-3 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="hidden sm:inline font-medium">Select User</span>
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-purple-600' : 'bg-gray-200'}`} />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="hidden sm:inline font-medium">Select Time</span>
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-purple-600' : 'bg-gray-200'}`} />
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span className="hidden sm:inline font-medium">Confirm</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Select User */}
          {step === 1 && (
            <div>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  data-testid="user-search-input"
                />
              </div>

              {/* User List */}
              {loadingUsers ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No users found</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filteredUsers.map(user => (
                    <button
                      key={user.id}
                      onClick={() => handleUserSelect(user)}
                      className="w-full p-4 border rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-left flex items-center gap-4"
                      data-testid={`user-option-${user.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {getUserDisplayName(user)}
                        </p>
                        <p className="text-sm text-gray-500 truncate flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </p>
                      </div>
                      <div className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                        {user.membership_tier || 'guest'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Time */}
          {step === 2 && (
            <div>
              {/* Selected User Badge */}
              <div className="mb-4 p-3 bg-purple-50 rounded-lg flex items-center gap-3">
                <User className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-gray-900">{getUserDisplayName(selectedUser)}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
                <button 
                  onClick={() => setStep(1)} 
                  className="ml-auto text-sm text-purple-600 hover:underline"
                >
                  Change
                </button>
              </div>

              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => changeMonth(-1)}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="font-medium">
                  {currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  onClick={() => changeMonth(1)}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                >
                  Next
                </button>
              </div>

              {/* Available Slots */}
              {loadingSlots ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No available slots this month</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupSlotsByDate()).map(([date, slots]) => (
                    <div key={date} className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 font-medium text-gray-700 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(date)}
                      </div>
                      <div className="p-3 flex flex-wrap gap-2">
                        {slots.map(slot => (
                          <button
                            key={`${slot.date}-${slot.time}`}
                            onClick={() => handleSlotSelect(slot)}
                            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                              selectedSlot?.date === slot.date && selectedSlot?.time === slot.time
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'hover:border-purple-500 hover:bg-purple-50'
                            }`}
                            data-testid={`slot-${slot.date}-${slot.time}`}
                          >
                            <Clock className="w-3 h-3 inline mr-1" />
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Appointment Summary</h3>
                
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Student</p>
                    <p className="font-medium">{getUserDisplayName(selectedUser)}</p>
                    <p className="text-sm text-gray-500">{selectedUser.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-medium">{formatDate(selectedSlot.date)} at {selectedSlot.time}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes for this appointment..."
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  rows={3}
                  data-testid="appointment-notes"
                />
              </div>

              {/* Info */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">The student will be notified</p>
                  <p>They will receive an email and push notification about this appointment.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={loading}
            >
              Back
            </Button>
          )}
          {step === 1 && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          
          <div className="ml-auto">
            {step === 2 && selectedSlot && (
              <Button onClick={() => setStep(3)}>
                Continue
              </Button>
            )}
            {step === 3 && (
              <Button
                onClick={handleBookAppointment}
                disabled={loading}
                data-testid="confirm-booking-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Booking...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Confirm Booking
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAppointmentBooking;
